import ChartCanvasMargins from './ChartCanvasMargins.jsx'
import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Activity,
  Bell,
  CalendarDays,
  CandlestickChart,
  Gauge,
  LineChart,
  Pencil,
  Settings2,
  X,
} from 'lucide-react'
import ColorOpacityControl from './ColorOpacityControl.jsx'

const TABS = [
  { key: 'symbol', label: 'Symbol', icon: CandlestickChart },
  { key: 'status', label: 'Status line', icon: LineChart },
  { key: 'scales', label: 'Scales and lines', icon: Gauge },
  { key: 'canvas', label: 'Canvas', icon: Pencil },
  { key: 'trading', label: 'Trading', icon: Activity },
  { key: 'alerts', label: 'Alerts', icon: Bell },
  { key: 'events', label: 'Events', icon: CalendarDays },
]

function CheckboxRow({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-xs text-ink cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="h-3.5 w-3.5 rounded border-border bg-canvas2 accent-ink"
      />
      <span>{label}</span>
    </label>
  )
}

CheckboxRow.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
}

function SelectShell({ children }) {
  return (
    <button
      type="button"
      className="h-7 min-w-[128px] rounded-md border border-border bg-canvas2 px-2.5 text-left text-xs text-inkMuted"
    >
      {children}
    </button>
  )
}

SelectShell.propTypes = {
  children: PropTypes.node.isRequired,
}

const MARKER_RANGE_OPTIONS = [
  { value: '30d', label: '30d' },
  { value: '6m', label: '6m' },
  { value: '1y', label: '1y' },
  { value: 'all', label: 'All' },
]

function ColorRow({ label, color, opacity, onColorChange, onOpacityChange, testPrefix }) {
  return (
    <>
      <div className="text-xs text-inkMuted">{label}</div>
      <ColorOpacityControl
        color={color}
        opacity={opacity}
        onColorChange={onColorChange}
        onOpacityChange={onOpacityChange}
        triggerTestId={`${testPrefix}-trigger`}
        swatchTestIdPrefix={`${testPrefix}-swatch`}
        hexInputTestId={`${testPrefix}-hex`}
        opacityTestId={`${testPrefix}-opacity`}
        ariaLabel={`${label} color`}
      />
    </>
  )
}

ColorRow.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string,
  opacity: PropTypes.number,
  onColorChange: PropTypes.func.isRequired,
  onOpacityChange: PropTypes.func.isRequired,
  testPrefix: PropTypes.string.isRequired,
}

function CandleColorRow({
  label,
  visible,
  onVisibleChange,
  upColor,
  downColor,
  upOpacity,
  downOpacity,
  onUpColorChange,
  onDownColorChange,
  onUpOpacityChange,
  onDownOpacityChange,
  testPrefix,
}) {
  return (
    <div className="grid grid-cols-[88px_1fr] items-center gap-3">
      <CheckboxRow
        label={label}
        checked={visible}
        onChange={onVisibleChange}
        testId={`${testPrefix}-visible`}
      />
      <div className="flex items-center gap-2">
        <ColorOpacityControl
          color={upColor}
          opacity={upOpacity}
          onColorChange={onUpColorChange}
          onOpacityChange={onUpOpacityChange}
          triggerTestId={`${testPrefix}-up-trigger`}
          swatchTestIdPrefix={`${testPrefix}-up-swatch`}
          hexInputTestId={`${testPrefix}-up-hex`}
          opacityTestId={`${testPrefix}-up-opacity`}
          ariaLabel={`${label} up color`}
        />
        <ColorOpacityControl
          color={downColor}
          opacity={downOpacity}
          onColorChange={onDownColorChange}
          onOpacityChange={onDownOpacityChange}
          triggerTestId={`${testPrefix}-down-trigger`}
          swatchTestIdPrefix={`${testPrefix}-down-swatch`}
          hexInputTestId={`${testPrefix}-down-hex`}
          opacityTestId={`${testPrefix}-down-opacity`}
          ariaLabel={`${label} down color`}
        />
      </div>
    </div>
  )
}

CandleColorRow.propTypes = {
  label: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  onVisibleChange: PropTypes.func.isRequired,
  upColor: PropTypes.string,
  downColor: PropTypes.string,
  upOpacity: PropTypes.number,
  downOpacity: PropTypes.number,
  onUpColorChange: PropTypes.func.isRequired,
  onDownColorChange: PropTypes.func.isRequired,
  onUpOpacityChange: PropTypes.func.isRequired,
  onDownOpacityChange: PropTypes.func.isRequired,
  testPrefix: PropTypes.string.isRequired,
}

export default function ChartSettingsModal({
  open,
  chartType,
  activeTab,
  onActiveTabChange,
  settings,
  onSettingsChange,
  quickMeasureEnabled = false,
  crosshairEnabled = false,
  retainedVwapEnabled = false,
  autoScale,
  logScale,
  onAutoScaleChange,
  onLogScaleChange,
  earningsVisible = true,
  onEarningsVisibleChange = () => {},
  executionMarkersVisible = true,
  onExecutionMarkersVisibleChange = () => {},
  markerRange,
  onMarkerRangeChange,
  activeTimeframe,
  activeTimeframeLabel,
  margins,
  sessionOpenViewEnabled = false,
  onMarginsChange,
  onClose,
}) {
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [dragState, setDragState] = useState(null)

  useEffect(() => {
    if (!open) return undefined
    function handleKeyDown(event) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  useEffect(() => {
    if (!dragState) return undefined
    function handlePointerMove(event) {
      setDragOffset({
        x: dragState.originX + event.clientX - dragState.startX,
        y: dragState.originY + event.clientY - dragState.startY,
      })
    }
    function handlePointerUp() {
      setDragState(null)
    }
    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)
    document.addEventListener('pointercancel', handlePointerUp)
    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
      document.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [dragState])

  if (!open) return null

  const ActiveIcon = TABS.find((t) => t.key === activeTab)?.icon || Settings2
  const isBarChart = chartType === 'bar'

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/10"
      data-testid="chart-settings-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Chart settings"
    >
      <div
        className="flex h-[min(410px,calc(100%-32px))] w-[min(560px,calc(100%-24px))] overflow-hidden rounded-lg border border-border bg-canvas text-ink shadow-2xl"
        data-testid="chart-settings-modal"
        style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}
      >
        <aside className="w-[160px] shrink-0 border-r border-border px-3 py-4">
          <div className="mb-5 text-lg font-semibold">Settings</div>
          <nav className="space-y-0.5">
            {TABS.map((tab) => {
              const Icon = tab.icon
              const active = tab.key === activeTab
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => onActiveTabChange(tab.key)}
                  data-testid={`chart-settings-tab-${tab.key}`}
                  className={`flex h-8 w-full items-center gap-2 rounded-md px-3 text-left text-xs transition-colors ${
                    active
                      ? 'bg-canvas2 text-ink font-semibold'
                      : 'text-inkMuted hover:bg-canvas2 hover:text-ink'
                  }`}
                >
                  <Icon size={15} strokeWidth={1.7} />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          <header
            className="flex h-11 shrink-0 cursor-move items-center justify-between border-b border-border px-5"
            data-testid="chart-settings-drag-handle"
            onPointerDown={(event) => {
              if (event.button !== 0) return
              event.preventDefault()
              setDragState({
                startX: event.clientX,
                startY: event.clientY,
                originX: dragOffset.x,
                originY: dragOffset.y,
              })
            }}
          >
            <div className="flex items-center gap-2 text-base font-semibold">
              <ActiveIcon size={17} strokeWidth={1.8} />
              <span>{TABS.find((t) => t.key === activeTab)?.label || 'Settings'}</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close settings"
              data-testid="chart-settings-close"
              onPointerDown={(event) => event.stopPropagation()}
              className="flex h-7 w-7 items-center justify-center rounded-md text-inkMuted hover:bg-canvas2 hover:text-ink"
            >
              <X size={18} strokeWidth={1.7} />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {activeTab === 'symbol' && (
              <div className="max-w-[360px] space-y-5">
                {isBarChart ? (
                  <section className="space-y-3">
                    <div className="text-[10px] uppercase tracking-wide text-inkMuted">Bars</div>
                    <CheckboxRow
                      label="Thick OHLC bars"
                      checked={settings.thickOhlcBars}
                      onChange={(value) => onSettingsChange({ thickOhlcBars: value })}
                      testId="chart-settings-thick-ohlc-bars"
                    />
                    <CheckboxRow
                      label="Color bars based on previous close"
                      checked={settings.colorBarsBasedOnPreviousClose}
                      onChange={(value) => onSettingsChange({ colorBarsBasedOnPreviousClose: value })}
                      testId="chart-settings-color-previous-close"
                    />
                    <div className="grid grid-cols-[105px_1fr] items-center gap-3">
                      <ColorRow
                        label="Up color"
                        color={settings.barUpColor}
                        opacity={settings.barUpOpacity}
                        onColorChange={(value) => onSettingsChange({ barUpColor: value })}
                        onOpacityChange={(value) => onSettingsChange({ barUpOpacity: value })}
                        testPrefix="chart-settings-bar-up-color"
                      />
                      <ColorRow
                        label="Down color"
                        color={settings.barDownColor}
                        opacity={settings.barDownOpacity}
                        onColorChange={(value) => onSettingsChange({ barDownColor: value })}
                        onOpacityChange={(value) => onSettingsChange({ barDownOpacity: value })}
                        testPrefix="chart-settings-bar-down-color"
                      />
                    </div>
                  </section>
                ) : (
                  <section className="space-y-3">
                    <div className="text-[10px] uppercase tracking-wide text-inkMuted">Candles</div>
                    <CheckboxRow
                      label="Color bars based on previous close"
                      checked={settings.colorBarsBasedOnPreviousClose}
                      onChange={(value) => onSettingsChange({ colorBarsBasedOnPreviousClose: value })}
                      testId="chart-settings-color-previous-close"
                    />
                    <div className="space-y-2">
                      <CandleColorRow
                        label="Body"
                        visible={settings.candleBodyVisible}
                        onVisibleChange={(value) => onSettingsChange({ candleBodyVisible: value })}
                        upColor={settings.candleBodyUpColor}
                        downColor={settings.candleBodyDownColor}
                        upOpacity={settings.candleBodyUpOpacity}
                        downOpacity={settings.candleBodyDownOpacity}
                        onUpColorChange={(value) => onSettingsChange({ candleBodyUpColor: value })}
                        onDownColorChange={(value) => onSettingsChange({ candleBodyDownColor: value })}
                        onUpOpacityChange={(value) => onSettingsChange({ candleBodyUpOpacity: value })}
                        onDownOpacityChange={(value) => onSettingsChange({ candleBodyDownOpacity: value })}
                        testPrefix="chart-settings-candle-body"
                      />
                      <CandleColorRow
                        label="Borders"
                        visible={settings.candleBorderVisible}
                        onVisibleChange={(value) => onSettingsChange({ candleBorderVisible: value })}
                        upColor={settings.candleBorderUpColor}
                        downColor={settings.candleBorderDownColor}
                        upOpacity={settings.candleBorderUpOpacity}
                        downOpacity={settings.candleBorderDownOpacity}
                        onUpColorChange={(value) => onSettingsChange({ candleBorderUpColor: value })}
                        onDownColorChange={(value) => onSettingsChange({ candleBorderDownColor: value })}
                        onUpOpacityChange={(value) => onSettingsChange({ candleBorderUpOpacity: value })}
                        onDownOpacityChange={(value) => onSettingsChange({ candleBorderDownOpacity: value })}
                        testPrefix="chart-settings-candle-border"
                      />
                      <CandleColorRow
                        label="Wick"
                        visible={settings.candleWickVisible}
                        onVisibleChange={(value) => onSettingsChange({ candleWickVisible: value })}
                        upColor={settings.candleWickUpColor}
                        downColor={settings.candleWickDownColor}
                        upOpacity={settings.candleWickUpOpacity}
                        downOpacity={settings.candleWickDownOpacity}
                        onUpColorChange={(value) => onSettingsChange({ candleWickUpColor: value })}
                        onDownColorChange={(value) => onSettingsChange({ candleWickDownColor: value })}
                        onUpOpacityChange={(value) => onSettingsChange({ candleWickUpOpacity: value })}
                        onDownOpacityChange={(value) => onSettingsChange({ candleWickDownOpacity: value })}
                        testPrefix="chart-settings-candle-wick"
                      />
                    </div>
                  </section>
                )}

                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Data modification</div>
                  <CheckboxRow
                    label="Adjust data for dividends"
                    checked={true}
                    onChange={() => {}}
                    testId="chart-settings-dividends"
                  />
                  <div className="grid grid-cols-[105px_1fr] items-center gap-3">
                    <div className="text-xs text-inkMuted">Precision</div>
                    <SelectShell>Default</SelectShell>
                    <div className="text-xs text-inkMuted">Timezone</div>
                    <SelectShell>(UTC-5) Chicago</SelectShell>
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'scales' && (
              <div className="max-w-[420px] space-y-5">
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Price scale</div>
                  <CheckboxRow
                    label="Auto scale"
                    checked={autoScale}
                    onChange={onAutoScaleChange}
                    testId="chart-settings-auto-scale"
                  />
                  <CheckboxRow
                    label="Log scale"
                    checked={logScale}
                    onChange={onLogScaleChange}
                    testId="chart-settings-log-scale"
                  />
                </section>
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Price labels and lines</div>
                  <div className="grid grid-cols-[145px_1fr] items-center gap-3">
                    <div className="text-xs text-inkMuted">Symbol</div>
                    <SelectShell>Value, line</SelectShell>
                    <div className="text-xs text-inkMuted">Indicators</div>
                    <SelectShell>Hidden</SelectShell>
                    <div className="text-xs text-inkMuted">High and low</div>
                    <SelectShell>Value</SelectShell>
                  </div>
                </section>
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Extended-session prices</div>
                  {[
                    ['Pre-market', 'preMarketPriceVisible'],
                    ['Post-market', 'postMarketPriceVisible'],
                    ['Overnight', 'overnightPriceVisible'],
                  ].map(([label, key]) => (
                    <label key={key} className="flex items-center justify-between gap-3 text-xs text-inkMuted">
                      {label}
                      <select
                        aria-label={`${label} price line and label`}
                        value={settings[key] === false ? 'hide' : 'show'}
                        onChange={event => onSettingsChange({ [key]: event.target.value === 'show' })}
                        className="h-7 rounded-md border border-border bg-canvas2 px-2.5 text-xs text-ink"
                      >
                        <option value="show">Show</option>
                        <option value="hide">Hide</option>
                      </select>
                    </label>
                  ))}
                  <div className="text-[11px] leading-4 text-inkMuted">
                    Show or hide each session’s price line and label together.
                  </div>
                </section>
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">VWAPs</div>
                  <label className="flex items-center justify-between gap-3 text-xs text-inkMuted">
                    VWAP labels
                    <select
                      aria-label="VWAP labels"
                      value={settings.vwapLabelsVisible === false ? 'hide' : 'show'}
                      onChange={event => onSettingsChange({ vwapLabelsVisible: event.target.value === 'show' })}
                      data-testid="chart-settings-vwap-labels-visible"
                      className="h-7 rounded-md border border-border bg-canvas2 px-2.5 text-xs text-ink"
                    >
                      <option value="show">Show</option>
                      <option value="hide">Hide</option>
                    </select>
                  </label>
                  <div className="text-[11px] leading-4 text-inkMuted">
                    Show price-axis labels for standard and anchored VWAPs. Lines and alerts stay active.
                  </div>
                </section>
                {retainedVwapEnabled && <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">AVWAP alerts</div>
                  <label className="flex items-center justify-between gap-3 text-xs text-inkMuted">
                    Alert icon position
                    <select aria-label="AVWAP alert icon position" value={settings.avwapAlertIconPosition || 'label'} onChange={e => onSettingsChange({ avwapAlertIconPosition: e.target.value })} className="h-7 rounded-md border border-border bg-canvas2 px-2.5 text-xs text-ink">
                      <option value="label">Price label</option><option value="line">AVWAP line</option>
                    </select>
                  </label>
                </section>}
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Position lines</div>
                  <div className="grid grid-cols-[105px_1fr] items-center gap-3">
                    <ColorRow
                      label="Trigger"
                      color={settings.positionEntryColor}
                      opacity={settings.positionEntryOpacity}
                      onColorChange={(value) => onSettingsChange({ positionEntryColor: value })}
                      onOpacityChange={(value) => onSettingsChange({ positionEntryOpacity: value })}
                      testPrefix="chart-settings-position-entry"
                    />
                    <ColorRow
                      label="Stop"
                      color={settings.positionStopColor}
                      opacity={settings.positionStopOpacity}
                      onColorChange={(value) => onSettingsChange({ positionStopColor: value })}
                      onOpacityChange={(value) => onSettingsChange({ positionStopOpacity: value })}
                      testPrefix="chart-settings-position-stop"
                    />
                    <ColorRow
                      label="1/3 Target"
                      color={settings.positionTargetColor}
                      opacity={settings.positionTargetOpacity}
                      onColorChange={(value) => onSettingsChange({ positionTargetColor: value })}
                      onOpacityChange={(value) => onSettingsChange({ positionTargetOpacity: value })}
                      testPrefix="chart-settings-position-target"
                    />
                    <ColorRow
                      label="Avg"
                      color={settings.positionAvgColor}
                      opacity={settings.positionAvgOpacity}
                      onColorChange={(value) => onSettingsChange({ positionAvgColor: value })}
                      onOpacityChange={(value) => onSettingsChange({ positionAvgOpacity: value })}
                      testPrefix="chart-settings-position-avg"
                    />
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'canvas' && (
              <div className="max-w-[420px] space-y-5">
                <section className="space-y-3">
                  <div className="text-xs font-medium text-ink">Indicators</div>
                  <CheckboxRow label="Show indicator dropdown" checked={settings.indicatorDropdownVisible !== false}
                    testId="chart-settings-indicator-dropdown-visible"
                    onChange={value => onSettingsChange({ indicatorDropdownVisible: value })} />
                  <p className="text-xs text-inkMuted">Hiding the dropdown keeps your indicators on the chart.</p>
                </section>
                <section className="space-y-3">
                  <div className="text-xs font-medium text-ink">Chart controls</div>
                  <CheckboxRow label="Show scale controls" checked={settings.scaleControlsVisible !== false}
                    testId="chart-settings-scale-controls-visible"
                    onChange={value => onSettingsChange({ scaleControlsVisible: value })} />
                  <p className="text-xs text-inkMuted">Show the Auto scale, Log scale and Reset view buttons.</p>
                  <CheckboxRow label="Show volume readout" checked={settings.volumeReadoutVisible !== false}
                    testId="chart-settings-volume-readout-visible"
                    onChange={value => onSettingsChange({ volumeReadoutVisible: value })} />
                </section>
                <section className="space-y-3">
                  <div className="text-xs font-medium text-ink">Axis label backgrounds</div>
                  <p className="text-xs text-inkMuted">Colors are saved for this chart and the active light or dark theme.</p>
                  <CheckboxRow label="Use candle or bar color for current price" checked={settings.currentPriceLabelCustom !== true}
                    testId="chart-settings-current-price-label-auto"
                    onChange={value => onSettingsChange({ currentPriceLabelCustom: !value })} />
                  <div className="grid grid-cols-[150px_1fr] items-center gap-3">
                    {[
                      { key: 'currentPriceLabelColor', label: 'Current price label', prefix: 'current-price-label' },
                      ...(crosshairEnabled ? [
                        { key: 'crosshairPriceLabelColor', label: 'Crosshair price label', prefix: 'crosshair-price-label' },
                        { key: 'dateLabelColor', label: 'Date label', prefix: 'date-label' },
                      ] : []),
                    ].map(({ key, label, prefix }) => <div key={key} className="contents">
                      <div className="text-xs text-inkMuted">{label}</div>
                      <ColorOpacityControl color={settings[key] || '#262626'}
                        onColorChange={value => onSettingsChange({ [key]: value, ...(key === 'currentPriceLabelColor' ? { currentPriceLabelCustom: true } : {}) })}
                        ariaLabel={`${label} color`} triggerTestId={`${prefix}-color`}
                        swatchTestIdPrefix={`${prefix}-swatch`} hexInputTestId={`${prefix}-hex`} opacityTestId={`${prefix}-opacity`} />
                    </div>)}
                  </div>
                </section>
                {crosshairEnabled && <section className="space-y-3 border-b border-border pb-4 mb-4">
                  <div className="text-xs font-medium text-ink">Crosshair</div>
                  <CheckboxRow label="Show crosshair" checked={settings.crosshairVisible !== false}
                    testId="chart-settings-crosshair-visible"
                    onChange={value => onSettingsChange({ crosshairVisible: value })} />
                  {['Vertical', 'Horizontal'].map(axis => {
                    const prefix = `crosshair${axis}`
                    return <div key={axis} className="space-y-2">
                      <CheckboxRow label={`${axis} line`} checked={settings[`${prefix}Visible`] !== false}
                        testId={`chart-settings-crosshair-${axis.toLowerCase()}-visible`}
                        onChange={value => onSettingsChange({ [`${prefix}Visible`]: value })} />
                      <div className="flex items-center gap-2">
                        <ColorOpacityControl color={settings[`${prefix}Color`] || '#A2A2A2'}
                          opacity={settings[`${prefix}Opacity`] ?? 32}
                          onColorChange={value => onSettingsChange({ [`${prefix}Color`]: value })}
                          onOpacityChange={value => onSettingsChange({ [`${prefix}Opacity`]: value })}
                          ariaLabel={`${axis} crosshair color`}
                          triggerTestId={`crosshair-${axis}-color`}
                          hexInputTestId={`crosshair-${axis}-hex`} opacityTestId={`crosshair-${axis}-opacity`} />
                        <select aria-label={`${axis} crosshair style`} value={settings[`${prefix}Style`] ?? 2}
                          className="h-7 rounded border border-border bg-canvas2 text-xs text-ink"
                          onChange={e => onSettingsChange({ [`${prefix}Style`]: Number(e.target.value) })}>
                          <option value={0}>Solid</option><option value={1}>Dotted</option><option value={2}>Dashed</option>
                        </select>
                        <select aria-label={`${axis} crosshair thickness`} value={settings[`${prefix}Width`] ?? 1}
                          className="h-7 rounded border border-border bg-canvas2 text-xs text-ink"
                          onChange={e => onSettingsChange({ [`${prefix}Width`]: Number(e.target.value) })}>
                          {[1,2,3,4].map(width => <option key={width} value={width}>{width} px</option>)}
                        </select>
                      </div>
                    </div>
                  })}
                </section>}
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Appearance</div>
                  <CheckboxRow
                    label="Match app theme"
                    checked={settings.matchAppTheme !== false}
                    onChange={(value) => onSettingsChange({ matchAppTheme: value })}
                    testId="chart-settings-match-app-theme"
                  />
                  {settings.matchAppTheme !== false ? (
                    <div className="rounded-md border border-border bg-canvas2 px-3 py-2 text-xs leading-5 text-inkMuted">
                      The chart canvas follows the active Erevis theme.
                    </div>
                  ) : (
                    <div className="grid grid-cols-[112px_1fr] items-center gap-3">
                      <ColorRow
                        label="Background"
                        color={settings.canvasBackgroundColor}
                        opacity={settings.canvasBackgroundOpacity}
                        onColorChange={(value) => onSettingsChange({ canvasBackgroundColor: value })}
                        onOpacityChange={(value) => onSettingsChange({ canvasBackgroundOpacity: value })}
                        testPrefix="chart-settings-canvas-background"
                      />
                      <ColorRow
                        label="Labels"
                        color={settings.canvasTextColor}
                        opacity={settings.canvasTextOpacity}
                        onColorChange={(value) => onSettingsChange({ canvasTextColor: value })}
                        onOpacityChange={(value) => onSettingsChange({ canvasTextOpacity: value })}
                        testPrefix="chart-settings-canvas-text"
                      />
                      <CheckboxRow
                        label="Grid lines"
                        checked={settings.canvasGridVisible === true}
                        onChange={(value) => onSettingsChange({ canvasGridVisible: value })}
                        testId="chart-settings-canvas-grid-visible"
                      />
                      <ColorOpacityControl
                        color={settings.canvasGridColor}
                        opacity={settings.canvasGridOpacity}
                        onColorChange={(value) => onSettingsChange({ canvasGridColor: value })}
                        onOpacityChange={(value) => onSettingsChange({ canvasGridOpacity: value })}
                        triggerTestId="chart-settings-canvas-grid-trigger"
                        swatchTestIdPrefix="chart-settings-canvas-grid-swatch"
                        hexInputTestId="chart-settings-canvas-grid-hex"
                        opacityTestId="chart-settings-canvas-grid-opacity"
                        ariaLabel="Grid lines color"
                      />
                    </div>
                  )}
                </section>
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Sessions</div>
                  <div className="grid grid-cols-[145px_1fr] items-center gap-3">
                    <ColorRow
                      label="Pre/post market hours background"
                      color={settings.extendedHoursBackgroundColor}
                      opacity={settings.extendedHoursBackgroundOpacity}
                      onColorChange={(value) => onSettingsChange({ extendedHoursBackgroundColor: value })}
                      onOpacityChange={(value) => onSettingsChange({ extendedHoursBackgroundOpacity: value })}
                      testPrefix="chart-settings-extended-hours-background"
                    />
                  </div>
                </section>
                {quickMeasureEnabled && (
                  <section className="space-y-3">
                    <div className="text-[10px] uppercase tracking-wide text-inkMuted">Measurement tool</div>
                    <div className="grid grid-cols-[112px_1fr] items-center gap-3">
                      <ColorRow
                        label="Positive"
                        color={settings.quickMeasureUpColor}
                        opacity={settings.quickMeasureUpOpacity}
                        onColorChange={(value) => onSettingsChange({ quickMeasureUpColor: value })}
                        onOpacityChange={(value) => onSettingsChange({ quickMeasureUpOpacity: value })}
                        testPrefix="chart-settings-quick-measure-up"
                      />
                      <ColorRow
                        label="Negative"
                        color={settings.quickMeasureDownColor}
                        opacity={settings.quickMeasureDownOpacity}
                        onColorChange={(value) => onSettingsChange({ quickMeasureDownColor: value })}
                        onOpacityChange={(value) => onSettingsChange({ quickMeasureDownOpacity: value })}
                        testPrefix="chart-settings-quick-measure-down"
                      />
                    </div>
                    <div className="text-[11px] leading-4 text-inkMuted">
                      Opacity controls the measured-area fill. Arrows and labels use the selected color.
                    </div>
                  </section>
                )}
                <ChartCanvasMargins {...{ activeTimeframe, activeTimeframeLabel, margins, onMarginsChange, sessionOpenViewEnabled }} />
              </div>
            )}

            {activeTab === 'events' && (
              <div className="max-w-[420px] space-y-5">
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Earnings</div>
                  <CheckboxRow label="Show earnings dates" checked={earningsVisible}
                    onChange={onEarningsVisibleChange} testId="chart-settings-earnings-visible" />
                  <p className="text-xs text-inkMuted">Show announcement dates and the next earnings date for stocks in this pane.</p>
                </section>
                <section className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-inkMuted">Execution markers</div>
                  <CheckboxRow label="Show execution markers" checked={executionMarkersVisible}
                    onChange={onExecutionMarkersVisibleChange} testId="chart-settings-execution-markers-visible" />
                  <p className="text-xs text-inkMuted">Show recorded fills for this symbol, including completed trades. Independent of position lines.</p>
                  <div className="grid grid-cols-[125px_1fr] items-center gap-3">
                    <div className="text-xs text-inkMuted">History window</div>
                    <div className="flex items-center gap-1" data-testid="chart-settings-marker-range">
                      {MARKER_RANGE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => onMarkerRangeChange(option.value)}
                          className={`h-7 min-w-10 rounded-md border px-2 text-xs transition-colors ${
                            markerRange === option.value
                              ? 'border-border bg-canvas2 text-ink font-semibold'
                              : 'border-transparent text-inkMuted hover:border-border hover:bg-canvas2 hover:text-ink'
                          }`}
                          data-testid={`chart-settings-marker-range-${option.value}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'alerts' && <section className="max-w-[390px] space-y-4">
              <h3 className="text-xs font-medium text-ink">Price alert lines</h3>
              <div className="grid grid-cols-[100px_1fr] items-center gap-3">
                <ColorRow label="Alert line" color={settings.alertLineColor ?? '#8A8D91'} opacity={settings.alertLineOpacity ?? 40} onColorChange={value=>onSettingsChange({alertLineColor:value})} onOpacityChange={value=>onSettingsChange({alertLineOpacity:value})} testPrefix="alert-line-color"/>
                <label htmlFor="alert-line-width" className="text-xs text-inkMuted">Thickness</label>
                <select id="alert-line-width" aria-label="Alert line thickness" className="rounded border border-border bg-canvas px-2 py-1 text-xs" value={settings.alertLineWidth ?? 1} onChange={e=>onSettingsChange({alertLineWidth:Number(e.target.value)})}>{[1,2,3].map(width=><option key={width} value={width}>{width} px</option>)}</select>
                <label htmlFor="alert-line-style" className="text-xs text-inkMuted">Style</label>
                <select id="alert-line-style" aria-label="Alert line style" className="rounded border border-border bg-canvas px-2 py-1 text-xs" value={settings.alertLineStyle ?? 2} onChange={e=>onSettingsChange({alertLineStyle:Number(e.target.value)})}><option value="0">Solid</option><option value="1">Dotted</option><option value="2">Dashed</option></select>
              </div>
              <p className="text-xs text-inkMuted">Hover an alert line to see its name and delete button. Styling does not change monitoring.</p>
            </section>}

            {activeTab !== 'alerts' && activeTab !== 'symbol' && activeTab !== 'scales' && activeTab !== 'canvas' && activeTab !== 'events' && (
              <div className="max-w-[390px] space-y-3">
                <div className="text-[10px] uppercase tracking-wide text-inkMuted">General</div>
                <CheckboxRow
                  label="Visible"
                  checked={true}
                  onChange={() => {}}
                  testId={`chart-settings-${activeTab}-visible`}
                />
              </div>
            )}
          </div>

          <footer className="flex h-11 shrink-0 items-center justify-between border-t border-border px-5">
            <button
              type="button"
              className="h-7 rounded-md border border-border px-3 text-xs text-inkMuted"
            >
              Template
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-7 rounded-md border border-border px-3 text-xs text-ink"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onClose}
                className="h-7 rounded-md bg-ink px-4 text-xs text-canvas"
              >
                Ok
              </button>
            </div>
          </footer>
        </section>
      </div>
    </div>
  )
}

ChartSettingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  chartType: PropTypes.oneOf(['candlestick', 'bar']).isRequired,
  activeTab: PropTypes.string.isRequired,
  onActiveTabChange: PropTypes.func.isRequired,
  settings: PropTypes.shape({
    thickOhlcBars: PropTypes.bool.isRequired,
    colorBarsBasedOnPreviousClose: PropTypes.bool.isRequired,
    barUpColor: PropTypes.string.isRequired,
    barDownColor: PropTypes.string.isRequired,
    barUpOpacity: PropTypes.number.isRequired,
    barDownOpacity: PropTypes.number.isRequired,
    candleBodyVisible: PropTypes.bool.isRequired,
    candleBodyUpColor: PropTypes.string.isRequired,
    candleBodyDownColor: PropTypes.string.isRequired,
    candleBodyUpOpacity: PropTypes.number.isRequired,
    candleBodyDownOpacity: PropTypes.number.isRequired,
    candleBorderVisible: PropTypes.bool.isRequired,
    candleBorderUpColor: PropTypes.string.isRequired,
    candleBorderDownColor: PropTypes.string.isRequired,
    candleBorderUpOpacity: PropTypes.number.isRequired,
    candleBorderDownOpacity: PropTypes.number.isRequired,
    candleWickVisible: PropTypes.bool.isRequired,
    candleWickUpColor: PropTypes.string.isRequired,
    candleWickDownColor: PropTypes.string.isRequired,
    candleWickUpOpacity: PropTypes.number.isRequired,
    candleWickDownOpacity: PropTypes.number.isRequired,
    extendedHoursBackgroundColor: PropTypes.string.isRequired,
    extendedHoursBackgroundOpacity: PropTypes.number.isRequired,
    quickMeasureUpColor: PropTypes.string.isRequired,
    quickMeasureUpOpacity: PropTypes.number.isRequired,
    quickMeasureDownColor: PropTypes.string.isRequired,
    quickMeasureDownOpacity: PropTypes.number.isRequired,
    positionEntryColor: PropTypes.string.isRequired,
    positionEntryOpacity: PropTypes.number.isRequired,
    positionStopColor: PropTypes.string.isRequired,
    positionStopOpacity: PropTypes.number.isRequired,
    positionTargetColor: PropTypes.string.isRequired,
    positionTargetOpacity: PropTypes.number.isRequired,
    positionAvgColor: PropTypes.string.isRequired,
    positionAvgOpacity: PropTypes.number.isRequired,
  }).isRequired,
  onSettingsChange: PropTypes.func.isRequired,
  quickMeasureEnabled: PropTypes.bool,
  crosshairEnabled: PropTypes.bool,
  retainedVwapEnabled: PropTypes.bool,
  autoScale: PropTypes.bool.isRequired,
  logScale: PropTypes.bool.isRequired,
  onAutoScaleChange: PropTypes.func.isRequired,
  onLogScaleChange: PropTypes.func.isRequired,
  earningsVisible: PropTypes.bool,
  onEarningsVisibleChange: PropTypes.func,
  executionMarkersVisible: PropTypes.bool,
  onExecutionMarkersVisibleChange: PropTypes.func,
  markerRange: PropTypes.oneOf(['30d', '6m', '1y', 'all']).isRequired,
  onMarkerRangeChange: PropTypes.func.isRequired,
  activeTimeframe: PropTypes.string.isRequired,
  activeTimeframeLabel: PropTypes.string.isRequired,
  margins: PropTypes.shape({
    top: PropTypes.number.isRequired,
    bottom: PropTypes.number.isRequired,
    rightOffset: PropTypes.number.isRequired,
    visibleBars: PropTypes.number.isRequired,
  }).isRequired,
  sessionOpenViewEnabled: PropTypes.bool,
  onMarginsChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}
